<template>
    <div class="card bg-white p-5 rounded-lg shadow-sm">

        <!-- Header -->
        <div class="flex items-start justify-between mb-4">
            <div>
                <h3 class="text-xl font-bold">School Course Registrations</h3>
                <p class="text-sm text-gray-500">View and filter student course registrations</p>
            </div>
            <Button label="Swap Course" icon="pi pi-arrow-right-arrow-left" size="small"
                severity="secondary" outlined @click="showSwapDialog = true" />
        </div>

        <!-- Filters -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3 mb-4">
            <div class="xl:col-span-2">
                <label class="block text-[11px] font-medium text-gray-500 mb-1">Search</label>
                <InputText v-model="filters.query" size="small" placeholder="Search by name or matric"
                    class="w-full rounded-lg" data-testid="course-reg-list-search" />
            </div>

            <div class="xl:col-span-2">
                <label class="block text-[11px] font-medium text-gray-500 mb-1">Academic Calendar</label>
                <AcademicCalendarSelect v-model="selectedCalendarId" size="small"
                    placeholder="Select Session" className="w-full rounded-lg"
                    data-testid="course-reg-list-calendar" />
            </div>

            <div>
                <label class="block text-[11px] font-medium text-gray-500 mb-1">Semester</label>
                <Select v-model="filters.semester" :options="semesters"
                    optionLabel="label" optionValue="value" showClear size="small"
                    placeholder="Select Semester" class="w-full rounded-lg" data-testid="course-reg-list-semester" />
            </div>

            <div>
                <label class="block text-[11px] font-medium text-gray-500 mb-1">Approval Status</label>
                <Select v-model="filters.approval_status" :options="approvalStatusFilterOptions"
                    optionLabel="label" optionValue="value" showClear size="small"
                    placeholder="All Statuses" class="w-full rounded-lg" data-testid="course-reg-list-approval-status" />
            </div>

            <div>
                <label class="block text-[11px] font-medium text-gray-500 mb-1">Programme</label>
                <Select v-model="filters.programme_id" :options="programmeOptions" filter
                    optionLabel="label" optionValue="value" showClear size="small"
                    placeholder="All Programmes" class="w-full rounded-lg" data-testid="course-reg-list-programme" />
            </div>

            <div>
                <label class="block text-[11px] font-medium text-gray-500 mb-1">Level</label>
                <Select v-model="filters.registration_level" :options="levels"
                    optionLabel="label" optionValue="value" showClear size="small"
                    placeholder="All Levels" class="w-full rounded-lg" data-testid="course-reg-list-level" />
            </div>
        </div>

        <!-- Bulk Action Bar -->
        <div v-if="selectedRows.length" class="flex items-center gap-3 mb-4 px-4 py-3 bg-primary/5 border border-primary/20 rounded-lg">
            <span class="text-sm font-medium text-gray-700">{{ selectedRows.length }} selected</span>
            <Button v-if="canApprove" label="Approve Selected" icon="pi pi-check" size="small"
                severity="success" :loading="bulkProcessing" @click="confirmBulkAction('APPROVE')" />
            <Button v-if="canEndorse" label="Endorse Selected" icon="pi pi-verified" size="small"
                severity="info" :loading="bulkProcessing" @click="confirmBulkAction('ENDORSE')" />
            <Button v-if="canSenateApprove" label="Senate Approve Selected" icon="pi pi-verified" size="small"
                severity="help" :loading="bulkProcessing" @click="confirmBulkAction('SENATE_APPROVE')" />
            <Button v-if="canSenateApprove" label="Refresh Result" icon="pi pi-refresh" size="small"
                severity="warn" outlined :loading="bulkRefreshing" @click="handleBulkRefresh()" />
            <Button v-if="canSenateApprove" label="Revoke Senate Approval" icon="pi pi-undo" size="small"
                severity="danger" outlined :loading="bulkUnpublishing" @click="handleBulkUnpublish()" />
            <Button v-if="canApprove || canEndorse" label="Reject Selected" icon="pi pi-times" size="small"
                severity="danger" :loading="bulkProcessing" @click="showRejectDialog = true" />
            <Button label="Update Details" icon="pi pi-pencil" size="small"
                severity="warn" outlined @click="showUpdateLevelDialog = true" />
            <Button label="Clear" icon="pi pi-filter-slash" text size="small" @click="selectedRows = []" />
            <div class="ml-auto" v-if="scopeFiltersActive">
                <Button v-if="canApprove" label="Approve All Filtered" icon="pi pi-check-circle" text size="small"
                    severity="success" :loading="bulkProcessing" @click="confirmScopeAction('APPROVE')" />
                <Button v-if="canEndorse" label="Endorse All Filtered" icon="pi pi-verified" text size="small"
                    severity="info" :loading="bulkProcessing" @click="confirmScopeAction('ENDORSE')" />
                <Button v-if="canSenateApprove" label="Senate Approve All Filtered" icon="pi pi-verified" text size="small"
                    severity="help" :loading="bulkProcessing" @click="confirmScopeAction('SENATE_APPROVE')" />
            </div>
        </div>

        <!-- Scope Actions (when no rows selected but filters active) -->
        <div v-else-if="scopeFiltersActive && !loading" class="flex items-center gap-3 mb-4 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
            <span class="text-xs text-gray-500">Bulk actions on all filtered results:</span>
            <Button v-if="canApprove" label="Approve All Filtered" icon="pi pi-check-circle" text size="small"
                severity="success" :loading="bulkProcessing" @click="confirmScopeAction('APPROVE')" />
            <Button v-if="canEndorse" label="Endorse All Filtered" icon="pi pi-verified" text size="small"
                severity="info" :loading="bulkProcessing" @click="confirmScopeAction('ENDORSE')" />
            <Button v-if="canSenateApprove" label="Senate Approve All Filtered" icon="pi pi-verified" text size="small"
                severity="help" :loading="bulkProcessing" @click="confirmScopeAction('SENATE_APPROVE')" />
        </div>

        <!-- Loading -->
        <template v-if="loading">
            <div class="flex items-center justify-center py-16">
                <span class="pi pi-spin pi-spinner text-3xl text-primary"></span>
            </div>
        </template>

        <!-- The request failed: say so instead of "No registrations found" -->
        <template v-else-if="error">
            <LoadErrorMessage what="the course registrations" :error="error" @retry="refetch()" />
        </template>

        <!-- Table -->
        <template v-else>
            <ReusablePrimeTable :headers="headers" :data="rows"
                emptyMessage="No registrations found"
                tableStyle="min-width: 60rem"
                :showCheckbox="true"
                v-model:selection="selectedRows"
                :paginator="true" :rows="rowsPerPage" :totalRecords="totalRecords"
                :rowsPerPageOptions="[10, 25, 50, 100, 200]"
                :first="firstRecord" :lazy="true" @page="onPageChange">

                <template #student="{ row }">
                    <div>
                        <div class="font-semibold">
                            {{ row.student?.first_name }} {{ row.student?.surname }}
                        </div>
                        <small class="text-gray-500">{{ row.student?.matric_no }}</small>
                    </div>
                </template>

                <template #semester_label="{ row }">
                    {{ semesterLabel(row.semester) }}
                </template>

                <template #programme_name="{ row }">
                    {{ row.programme?.program_name || '-' }}
                </template>

                <template #approval_status="{ row }">
                    <Tag :value="approvalStatusLabel(row.approval_status)"
                        :severity="getStatusSeverity(row.approval_status)" />
                </template>

                <template #senate_approval_status="{ row }">
                    <Tag :value="row.senate_approval_status || 'Pending'"
                        :severity="getStatusSeverity(row.senate_approval_status)" />
                </template>

                <template #courses_count="{ row }">
                    {{ row.coursesRegistered?.length || 0 }}
                </template>

                <template #created="{ row }">
                    {{ formatDate(row.createdAt) }}
                </template>

                <template #actions="{ row }">
                    <div class="flex gap-1 items-center">
                        <Button v-if="canView" icon="pi pi-eye"
                            class="p-button-text p-button-rounded p-button-sm"
                            v-tooltip="'View'"
                            :data-testid="`course-reg-view-${row.id}`"
                            @click="handleView(row)" />
                        <Button v-if="canUpdate" icon="pi pi-pencil"
                            class="p-button-text p-button-rounded p-button-sm"
                            severity="warn" v-tooltip="'Edit'"
                            :data-testid="`course-reg-edit-${row.id}`"
                            @click="handleEdit(row)" />
                        <Button v-if="canUpdate && canReleaseToSenate(row)" icon="pi pi-verified"
                            class="p-button-text p-button-rounded p-button-sm"
                            severity="success"
                            v-tooltip="'Senate Approve'"
                            :loading="senateApproving"
                            :data-testid="`course-reg-senate-${row.id}`"
                            @click="handleSenateApprove(row)" />
                        <Button v-if="canSenateApprove && row.senate_approval_status === SENATE_APPROVED" icon="pi pi-refresh"
                            class="p-button-text p-button-rounded p-button-sm"
                            severity="warn"
                            v-tooltip="'Refresh Result'"
                            :loading="republishing"
                            @click="handleRepublish(row)" />
                        <Button v-if="canSenateApprove && row.senate_approval_status === SENATE_APPROVED" icon="pi pi-undo"
                            class="p-button-text p-button-rounded p-button-sm"
                            severity="danger"
                            v-tooltip="'Revoke Senate Approval'"
                            :loading="unpublishing"
                            @click="handleUnpublish(row)" />
                    </div>
                </template>
            </ReusablePrimeTable>
        </template>

        <!-- Bulk Confirm Dialog -->
        <Dialog v-model:visible="showBulkConfirmDialog" :header="`${bulkActionVerb} Registrations`" modal :style="{ width: '480px' }">
            <p class="text-sm text-gray-600">
                Are you sure you want to <strong>{{ bulkActionVerb.toLowerCase() }}</strong>
                {{ bulkActionMode === 'selection' ? `${selectedRows.length} selected registration(s)?` : 'all registrations matching the current filters?' }}
            </p>
            <div v-if="bulkActionMode === 'scope'" class="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 space-y-1">
                <div class="font-semibold text-gray-700 mb-1">Active filters:</div>
                <div v-if="selectedCalendarId">
                    <i class="pi pi-calendar text-[10px] mr-1"></i> Academic Calendar: <strong>{{ selectedCalendarLabel }}</strong>
                </div>
                <div v-if="filters.semester">
                    <i class="pi pi-bookmark text-[10px] mr-1"></i> Semester: <strong>{{ semesters.find(s => s.value === filters.semester)?.label || `Semester ${filters.semester}` }}</strong>
                </div>
                <div v-if="filters.programme_id">
                    <i class="pi pi-book text-[10px] mr-1"></i> Programme: <strong>{{ programmeOptions.find(p => p.value === filters.programme_id)?.label || `#${filters.programme_id}` }}</strong>
                </div>
                <div v-if="filters.registration_level">
                    <i class="pi pi-sort-numeric-up text-[10px] mr-1"></i> Level: <strong>{{ filters.registration_level }}</strong>
                </div>
                <div v-if="filters.approval_status">
                    <i class="pi pi-check-circle text-[10px] mr-1"></i> Approval Status: <strong>{{ approvalStatusFilterOptions.find(s => s.value === filters.approval_status)?.label || filters.approval_status }}</strong>
                </div>
                <div v-if="!selectedCalendarId && !filters.semester && !filters.programme_id && !filters.registration_level" class="italic text-gray-400">
                    Defaults to active academic calendar
                </div>
            </div>
            <div class="flex justify-end gap-2 mt-4">
                <Button size="small" label="Cancel" severity="secondary" @click="showBulkConfirmDialog = false" />
                <Button size="small" :label="bulkActionVerb" :severity="bulkActionVerb === 'Reject' ? 'danger' : 'success'"
                    :loading="bulkProcessing" @click="executeBulkAction" />
            </div>
        </Dialog>

        <!-- Reject Reason Dialog -->
        <Dialog v-model:visible="showRejectDialog" header="Reject Registrations" modal :style="{ width: '420px' }">
            <div class="space-y-3">
                <p class="text-sm text-gray-600">
                    Rejecting {{ selectedRows.length }} registration(s). Please provide a reason.
                </p>
                <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Reason *</label>
                    <InputText v-model="rejectReason" class="w-full" placeholder="Enter rejection reason" />
                </div>
            </div>
            <div class="flex justify-end gap-2 mt-4">
                <Button size="small" label="Cancel" severity="secondary" @click="showRejectDialog = false" />
                <Button size="small" label="Reject" severity="danger" :loading="bulkProcessing"
                    :disabled="!rejectReason.trim()" @click="executeBulkReject" />
            </div>
        </Dialog>

        <!-- Swap Course Dialog -->
        <Dialog v-model:visible="showSwapDialog" header="Swap Course for Programme" modal :style="{ width: '520px' }">
            <div class="space-y-3 pt-2">
                <p class="text-xs text-gray-500">Replace one course with another across all registrations for a programme. Optionally scope to a specific academic calendar.</p>
                <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Programme *</label>
                    <Select v-model="swapForm.programme_id" :options="programmeOptions" filter
                        optionLabel="label" optionValue="value" placeholder="Select programme"
                        size="small" class="w-full" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Academic Calendar <span class="text-gray-400">(optional)</span></label>
                    <AcademicCalendarSelect v-model="swapForm.academic_calendar_id" size="small"
                        placeholder="All calendars" className="w-full" showClear />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Old Course (to replace) *</label>
                    <CourseSelect v-model="swapForm.old_course_id" size="small" placeholder="Search course to replace..." className="w-full" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">New Course (replacement) *</label>
                    <CourseSelect v-model="swapForm.new_course_id" size="small" placeholder="Search replacement course..." className="w-full" />
                </div>
            </div>
            <div class="flex justify-end gap-2 mt-4">
                <Button size="small" label="Cancel" severity="secondary" @click="showSwapDialog = false" />
                <Button size="small" label="Swap Course" icon="pi pi-arrow-right-arrow-left" severity="warn"
                    :loading="swapping"
                    :disabled="!swapForm.programme_id || !swapForm.old_course_id || !swapForm.new_course_id"
                    @click="executeSwap" />
            </div>
        </Dialog>

        <!-- Bulk Update Dialog -->
        <Dialog v-model:visible="showUpdateLevelDialog" header="Update Registration Details" modal :style="{ width: '420px' }">
            <div class="space-y-3 pt-2">
                <p class="text-sm text-gray-600">Update details for <strong>{{ selectedRows.length }}</strong> selected registration(s). Only filled fields will be changed.</p>
                <div>
                    <label class="block text-xs font-medium text-gray-500 mb-1">Programme</label>
                    <Select v-model="newProgrammeId" :options="programmeOptions" optionLabel="label" optionValue="value"
                        placeholder="No change" class="w-full" filter showClear />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-500 mb-1">Level</label>
                    <Select v-model="newLevel" :options="levels" optionLabel="label" optionValue="value"
                        placeholder="No change" class="w-full" showClear />
                </div>
            </div>
            <template #footer>
                <Button size="small" label="Cancel" severity="secondary" @click="showUpdateLevelDialog = false" />
                <Button size="small" label="Update" icon="pi pi-check" severity="warn"
                    :loading="bulkProcessing" :disabled="!newLevel && !newProgrammeId" @click="executeBulkUpdateLevel" />
            </template>
        </Dialog>

        <!-- Edit Dialog -->
        <Dialog v-model:visible="showEditDialog" header="Update Registration" modal
            :style="{ width: '520px' }">
            <div class="space-y-4 pt-2">
                <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Academic Calendar</label>
                    <AcademicCalendarSelect v-model="editForm.academic_calendar_id"
                        placeholder="Select Session" className="w-full"
                        data-testid="course-reg-edit-calendar" />
                    <small v-if="getErrorMessage('academic_calendar_id')" class="text-red-500">
                        {{ getErrorMessage('academic_calendar_id') }}
                    </small>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-gray-600">Programme</label>
                    <Select v-model="editForm.programme_id" :options="programmeOptions" optionLabel="label" optionValue="value"
                        placeholder="No change" class="w-full" filter showClear data-testid="course-reg-edit-programme" />
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-gray-600">Semester</label>
                        <Select v-model="editForm.semester" :options="semesters"
                            optionLabel="label" optionValue="value"
                            placeholder="Select Semester" class="w-full"
                            data-testid="course-reg-edit-semester" />
                        <small v-if="getErrorMessage('semester')" class="text-red-500">
                            {{ getErrorMessage('semester') }}
                        </small>
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-gray-600">Registration Level</label>
                        <Select v-model="editForm.registration_level" :options="levels"
                            optionLabel="label" optionValue="value"
                            placeholder="Select Level" class="w-full"
                            data-testid="course-reg-edit-level" />
                        <small v-if="getErrorMessage('registration_level')" class="text-red-500">
                            {{ getErrorMessage('registration_level') }}
                        </small>
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-gray-600">Approval Status</label>
                        <Select v-model="editForm.approval_status" :options="approvalStatuses"
                            optionLabel="label" optionValue="value" showClear
                            placeholder="Select Status" class="w-full"
                            data-testid="course-reg-edit-approval-status" />
                        <small v-if="getErrorMessage('approval_status')" class="text-red-500">
                            {{ getErrorMessage('approval_status') }}
                        </small>
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-xs font-medium text-gray-600">Senate Approval</label>
                        <Select v-model="editForm.senate_approval_status" :options="senateStatuses"
                            optionLabel="label" optionValue="value" showClear
                            placeholder="Select Status" class="w-full"
                            data-testid="course-reg-edit-senate-status" />
                        <small v-if="getErrorMessage('senate_approval_status')" class="text-red-500">
                            {{ getErrorMessage('senate_approval_status') }}
                        </small>
                    </div>
                </div>
            </div>

            <div class="flex justify-end gap-2 mt-6">
                <Button size="small" label="Cancel" severity="secondary"
                    data-testid="course-reg-edit-cancel-btn"
                    @click="showEditDialog = false" />
                <Button size="small" label="Save Changes"
                    :loading="updatingRegistrationFields"
                    data-testid="course-reg-edit-save-btn"
                    @click="confirmUpdate" />
            </div>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import ReusablePrimeTable from '@/components/common/ReusablePrimeTable.vue';
import LoadErrorMessage from '@/components/common/LoadErrorMessage.vue';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import CourseSelect from '@/components/common/CourseSelect.vue';

import gql from 'graphql-tag';
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useSchoolCourseRegistration } from '@/composables/queries/useSchoolCourseRegistration';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useFormErrors } from '@/composables/useFormErrors';
import { debounce } from '@/utils';
import { getStatusSeverity } from '@/utils/statusSeverity';
import { REGISTRATION_APPROVAL_STATUSES, registrationApprovalLabel } from '@/utils/registrationApprovalStatus';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { BULK_COURSE_REGISTRATION_ACTION, BULK_SWAP_COURSE, BULK_UPDATE_REGISTRATION_LEVEL, PUBLISH_RESULT } from '@/graphql/mutations/tertiary';
import { buildSelectionBulkInput, buildScopeBulkInput, hasScopeCriteria } from '@/utils/bulkRegistrationActions';
import { ApprovalActionEnum } from '@/types/composition-functions';
import { buildChangedRegistrationFields } from './courseRegistrationEdit';
import { buildUnpublishTargets, SENATE_APPROVED, type UnpublishableRow } from './unpublishSelection';
import { errorMessageOf } from '@/utils/errorMessage';

import type {
  StudentCourseRegistration,
} from '@/types/composition-functions';

const router = useRouter();
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const userStore = useUserStore();
const currentUser = computed(() => userStore.currentUser);

const toast = useToast();
const canView = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.View, AppPermissions.CourseRegistrationApproval.Resource),
);
const canUpdate = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Update, AppPermissions.CourseRegistrationApproval.Resource),
);
const canApprove = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Approve, AppPermissions.CourseRegistrationApproval.Resource),
);
const canEndorse = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Endorse, AppPermissions.CourseRegistrationApproval.Resource),
);
// Senate approval is its own backend permission (course_registration.senate_approve).
// Gating on Update instead would show the action to staff the mutation then rejects.
const canSenateApprove = computed(() =>
  can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.SenateApprove, AppPermissions.CourseRegistrationApproval.Resource),
);

/* ---------- BULK ACTIONS ---------- */
// Rows here are individual StudentCourseRegistration records, so row.id IS
// the registration id the backend's bulk mutation expects.
// Bulk unpublish needs the student/calendar pair off each selected row, so the
// selection type carries them rather than being narrowed to just the id.
const selectedRows = ref<UnpublishableRow[]>([]);
const bulkProcessing = ref(false);
const showBulkConfirmDialog = ref(false);
const showRejectDialog = ref(false);
const showUpdateLevelDialog = ref(false);
const newLevel = ref<string | null>(null);
const newProgrammeId = ref<string | null>(null);
const rejectReason = ref('');
const pendingBulkAction = ref<ApprovalActionEnum | null>(null);
const bulkActionMode = ref<'selection' | 'scope'>('selection');
const bulkActionVerb = computed(() => {
  if (pendingBulkAction.value === ApprovalActionEnum.Approve) return 'Approve';
  if (pendingBulkAction.value === ApprovalActionEnum.Endorse) return 'Endorse';
  if (pendingBulkAction.value === ApprovalActionEnum.Reject) return 'Reject';
  if (pendingBulkAction.value === ApprovalActionEnum.SenateApprove) return 'Senate Approve';
  return 'Process';
});

const { mutate: bulkActionMutate } = useMutation(BULK_COURSE_REGISTRATION_ACTION);
const { mutate: bulkUpdateLevelMutate } = useMutation(BULK_UPDATE_REGISTRATION_LEVEL);

function confirmBulkAction(action: string) {
  pendingBulkAction.value = action as ApprovalActionEnum;
  bulkActionMode.value = 'selection';
  showBulkConfirmDialog.value = true;
}

function confirmScopeAction(action: string) {
  pendingBulkAction.value = action as ApprovalActionEnum;
  bulkActionMode.value = 'scope';
  showBulkConfirmDialog.value = true;
}

async function executeBulkAction() {
  if (!pendingBulkAction.value) return;
  bulkProcessing.value = true;
  try {
    let input;
    if (bulkActionMode.value === 'selection') {
      const regIds = selectedRows.value.map((r) => String(r.id));
      input = buildSelectionBulkInput(pendingBulkAction.value, regIds);
    } else {
      input = buildScopeBulkInput(pendingBulkAction.value, currentScopeFilters.value);
    }
    const result = await bulkActionMutate({ input });
    const data = result?.data?.bulkCourseRegistrationAction;
    if (data) {
      const severity = data.affected_count > 0 ? 'success' : 'warn';
      toast.add({ severity, summary: bulkActionVerb.value, detail: data.message, life: 5000 });
    }
    selectedRows.value = [];
    showBulkConfirmDialog.value = false;
    await refetch();
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
  } finally {
    bulkProcessing.value = false;
  }
}

async function executeBulkReject() {
  if (!rejectReason.value.trim()) return;
  bulkProcessing.value = true;
  try {
    const regIds = selectedRows.value.map((r) => String(r.id));
    const input = buildSelectionBulkInput(ApprovalActionEnum.Reject, regIds, rejectReason.value);
    const result = await bulkActionMutate({ input });
    const data = result?.data?.bulkCourseRegistrationAction;
    if (data) {
      const severity = data.affected_count > 0 ? 'success' : 'warn';
      toast.add({ severity, summary: 'Rejected', detail: data.message, life: 5000 });
    }
    selectedRows.value = [];
    rejectReason.value = '';
    showRejectDialog.value = false;
    await refetch();
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
  } finally {
    bulkProcessing.value = false;
  }
}

async function executeBulkUpdateLevel() {
  if (!newLevel.value && !newProgrammeId.value) return;
  bulkProcessing.value = true;
  try {
    const regIds = selectedRows.value.map((r) => String(r.id));
    const vars: Record<string, unknown> = { registration_ids: regIds };
    if (newLevel.value) vars.registration_level = newLevel.value;
    if (newProgrammeId.value) vars.programme_id = newProgrammeId.value;

    const result = await bulkUpdateLevelMutate(vars);
    const data = result?.data?.bulkUpdateRegistrationLevel;
    if (!data?.success) {
      toast.add({ severity: 'error', summary: 'Update Failed', detail: data?.message || 'Failed to update.', life: 5000 });
      return;
    }
    toast.add({ severity: 'success', summary: 'Updated', detail: data.message, life: 5000 });
    selectedRows.value = [];
    newLevel.value = null;
    newProgrammeId.value = null;
    showUpdateLevelDialog.value = false;
    await refetch();
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
  } finally {
    bulkProcessing.value = false;
  }
}

const {
  updateRegistrationFields,
  updatingRegistrationFields,
  senateApproveRegistration,
  senateApproving,
} = useTertiaryMutations();

const {
  registrations,
  paginatorInfo,
  loading,
  error,
  refetch,
  setPage,
  setItemsPerPage,
  setFilter,
  setAcademicCalendar,
} = useSchoolCourseRegistration({ autoFetch: true, first: 10, page: 1 });

/* ---------- FILTER STATE ---------- */
const filters = ref<{
    query: string;
    semester: number | null;
    approval_status: string | null;
    programme_id: number | null;
    registration_level: string | null;
}>({
  query: '',
  semester: null,
  approval_status: null,
  programme_id: null,
  registration_level: null,
});

const selectedCalendarId = ref<string | number | null>(null);

const selectedCalendarLabel = computed(() => {
  if (!selectedCalendarId.value) return 'Active calendar';
  const cal = (academicCalendars.value || []).find(
    (c: { id: string | number; session?: { name?: string } | null; termStructure?: { name?: string } | null }) =>
      String(c.id) === String(selectedCalendarId.value),
  );
  if (cal) {
    return `${cal.session?.name || ''} - ${cal.termStructure?.name || ''}`.trim();
  }
  return `Calendar #${selectedCalendarId.value}`;
});

const currentScopeFilters = computed(() => ({
  level: filters.value.registration_level,
  academicCalendarId: selectedCalendarId.value ? String(selectedCalendarId.value) : null,
  programmeId: filters.value.programme_id ? String(filters.value.programme_id) : null,
  departmentId: null,
}));

const scopeFiltersActive = computed(() =>
  hasScopeCriteria(currentScopeFilters.value)
    || filters.value.semester !== null,
);

// Clear selection on page/filter change
watch([() => filters.value.semester, () => filters.value.approval_status, () => filters.value.programme_id, () => filters.value.registration_level, selectedCalendarId], () => {
  selectedRows.value = [];
});

const semesters = [
  { label: 'First Semester', value: 1 },
  { label: 'Second Semester', value: 2 },
  { label: 'Third Semester', value: 3 },
  { label: 'Fourth Semester', value: 4 },
];

const approvalStatusFilterOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Pending (legacy)', value: 'Pending' },
  { label: 'Level Adviser Approved', value: 'level_adviser_approved' },
  { label: 'Approved (legacy)', value: 'Approved' },
  { label: 'HOD Endorsed', value: 'hod_endorsed' },
  { label: 'Rejected', value: 'rejected' },
];

const { programmes: allProgrammes } = useProgrammes({ autoFetch: true, first: 200 });
const programmeOptions = computed(() =>
  (allProgrammes.value || [])
    .map((p: { id: string | number; program_name: string; prog_code: string }) =>
      ({ label: `${p.program_name} (${p.prog_code})`, value: String(p.id) }))
    .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)),
);

const { classrooms, academicCalendars } = useSchoolQuery();

const levels = computed(() =>
  (classrooms.value || [])
    .map((c) => {
      const name = c?.name || '';
      return name.trim()
        ? { label: name, value: name }
        : null;
    })
    .filter((o): o is { label: string; value: string } => o !== null),
);

const approvalStatuses = REGISTRATION_APPROVAL_STATUSES;

const senateStatuses = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Approved', value: 'Approved' },
];

function approvalStatusLabel(status?: string | null): string {
  return registrationApprovalLabel(status);
}

/* ---------- TABLE ---------- */
const headers = [
  { field: 'student', header: 'Student' },
  { field: 'academic_session', header: 'Session' },
  { field: 'semester_label', header: 'Semester' },
  { field: 'registration_level', header: 'Level' },
  { field: 'programme_name', header: 'Programme' },
  { field: 'approval_status', header: 'Reg Status' },
  { field: 'senate_approval_status', header: 'Senate Status' },
  { field: 'created', header: 'Created' },
  { field: 'actions', header: 'Actions', type: 'actions' },
];

const rows = computed(() => registrations.value || []);
const localRowsPerPage = ref(10);
const rowsPerPage = computed(() => localRowsPerPage.value);
const totalRecords = computed(() => Number(paginatorInfo.value?.total) || 0);
const firstRecord = computed(
  () => (((Number(paginatorInfo.value?.currentPage) || 1) - 1) * rowsPerPage.value) || 0,
);

/* ---------- HELPERS ---------- */
function semesterLabel(s?: number) {
  return s === 1 ? 'First Semester' : s === 2 ? 'Second Semester' : `Semester ${s ?? ''}`;
}

function formatDate(date?: string) {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
}

/* ---------- FILTERS ---------- */
// Calendar is sent as the top-level `academic_calendar_id` query argument and persists
// across refetches. Search runs globally, so it skips the calendar for that one refetch.
function buildFilterPayload() {
  return {
    query: filters.value.query || undefined,
    semester: filters.value.semester ?? undefined,
    approval_status: filters.value.approval_status || undefined,
    programme_id: filters.value.programme_id ?? undefined,
    registration_level: filters.value.registration_level ?? undefined,
  };
}

async function applyFilter() {
  await setFilter(buildFilterPayload());
}

const applySearchDebounced = debounce(async() => {
  if (filters.value.query.trim()) {
    // Search across all sessions
    await setFilter(buildFilterPayload(), { ignoreAcademicCalendar: true });
  } else {
    // Search cleared — revert to calendar-scoped results
    await applyFilter();
  }
}, 300);

watch(
  () => filters.value.query,
  () => {
    applySearchDebounced();
  },
);

watch(
  [() => filters.value.semester, () => filters.value.approval_status, () => filters.value.programme_id, () => filters.value.registration_level],
  () => {
    applyFilter();
  },
);

watch(selectedCalendarId, (newVal) => {
  const parsed = newVal != null && newVal !== ''
    ? parseInt(String(newVal), 10)
    : null;
  void setAcademicCalendar(Number.isFinite(parsed) && parsed !== null ? parsed : null);
});

/* ---------- PAGINATION ---------- */
async function onPageChange(event: { page?: number; first?: number; rows?: number }) {
  const eventRows = event.rows ?? rowsPerPage.value;
  const eventPage = typeof event.page === 'number'
    ? event.page + 1
    : Math.floor((event.first ?? 0) / eventRows) + 1;

  if (eventRows !== localRowsPerPage.value) {
    localRowsPerPage.value = eventRows;
    await setItemsPerPage(eventRows);
  } else {
    await setPage(eventPage);
  }
}

/* ---------- VIEW ---------- */
function handleView(row: StudentCourseRegistration) {
  if (!row?.student_id) return;
  router.push({
    name: 'admin-course-registration-view',
    params: { studentId: String(row.student_id) },
  });
}

/* ---------- SENATE APPROVAL (release results) ---------- */
// Senate can only release results once the registration is HOD-endorsed and not already released.
function canReleaseToSenate(row: StudentCourseRegistration) {
  return row?.approval_status === 'hod_endorsed' && row?.senate_approval_status !== SENATE_APPROVED;
}

async function handleSenateApprove(row: StudentCourseRegistration) {
  if (!row?.student_id || row?.academic_calendar_id == null) return;
  try {
    await senateApproveRegistration({
      student_id: String(row.student_id),
      academic_calendar_id: String(row.academic_calendar_id),
    });
    await refetch();
  } catch {
    // Error toast already surfaced by the composable.
  }
}

/* ---------- RE-PUBLISH / UNPUBLISH RESULT ---------- */
const republishing = ref(false);
const unpublishing = ref(false);
const bulkRefreshing = ref(false);
const bulkUnpublishing = ref(false);
const { mutate: publishMutate } = useMutation(PUBLISH_RESULT);

const UNPUBLISH_RESULT = gql`
  mutation UnPublishStudentResult($academic_calendar_id: ID!, $student_id: ID!) {
    unPublishStudentResult(academic_calendar_id: $academic_calendar_id, student_id: $student_id) {
      id
      status
    }
  }
`;
const { mutate: unpublishMutate } = useMutation(UNPUBLISH_RESULT);

async function handleRepublish(row: StudentCourseRegistration) {
  if (!row?.student_id || row?.academic_calendar_id == null) return;
  republishing.value = true;
  try {
    await publishMutate({
      studentId: String(row.student_id),
      calendarId: String(row.academic_calendar_id),
    });
    toast.add({ severity: 'success', summary: 'Result refreshed', detail: 'The result snapshot has been refreshed.', life: 3000 });
    await refetch();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Refresh failed', detail: errorMessageOf(e, 'Could not refresh result.'), life: 4000 });
  } finally {
    republishing.value = false;
  }
}

async function handleBulkRefresh() {
  const targets = selectedRows.value
    .filter((r) => r.senate_approval_status === SENATE_APPROVED && r.student_id && r.academic_calendar_id != null)
    .map((r) => ({ studentId: String(r.student_id), calendarId: String(r.academic_calendar_id) }));

  const unique = [...new Map(targets.map((t) => [`${t.studentId}-${t.calendarId}`, t])).values()];

  if (unique.length === 0) {
    toast.add({ severity: 'warn', summary: 'No approved results', detail: 'None of the selected rows have senate-approved results to refresh.', life: 3000 });
    return;
  }

  bulkRefreshing.value = true;
  let succeeded = 0;
  let failed = 0;

  try {
    for (const target of unique) {
      try {
        await publishMutate(target);
        succeeded++;
      } catch {
        failed++;
      }
    }

    if (succeeded) {
      toast.add({ severity: 'success', summary: 'Results refreshed', detail: `${succeeded} result(s) refreshed.`, life: 3000 });
    }
    if (failed) {
      toast.add({ severity: 'error', summary: 'Some results could not be refreshed', detail: `${failed} failed. Please retry those rows.`, life: 5000 });
    }

    selectedRows.value = [];
    await refetch();
  } finally {
    bulkRefreshing.value = false;
  }
}

async function handleUnpublish(row: StudentCourseRegistration) {
  if (!row?.student_id || row?.academic_calendar_id == null) return;
  unpublishing.value = true;
  try {
    await unpublishMutate({
      student_id: String(row.student_id),
      academic_calendar_id: String(row.academic_calendar_id),
    });
    toast.add({ severity: 'success', summary: 'Senate approval revoked', detail: 'The senate approval and published result have been removed.', life: 3000 });
    await refetch();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Revoke failed', detail: errorMessageOf(e, 'Could not revoke senate approval.'), life: 4000 });
  } finally {
    unpublishing.value = false;
  }
}

/**
 * Unpublish every selected registration that actually has a published result.
 *
 * Deliberately selection-only. There is no bulk unpublish mutation, so a
 * "unpublish all filtered" variant could only loop the rows already on screen —
 * it reported the page count while claiming to have covered the whole filter,
 * which is the worst possible outcome for a destructive action.
 */
async function handleBulkUnpublish() {
  const targets = buildUnpublishTargets(selectedRows.value);

  if (targets.length === 0) {
    toast.add({ severity: 'warn', summary: 'No senate-approved results', detail: 'None of the selected rows have senate-approved results.', life: 3000 });
    return;
  }

  bulkUnpublishing.value = true;
  let succeeded = 0;
  let failed = 0;

  try {
    for (const target of targets) {
      try {
        await unpublishMutate(target);
        succeeded++;
      } catch {
        // One student's failure must not silently abandon the rest of the batch.
        failed++;
      }
    }

    if (succeeded) {
      toast.add({ severity: 'success', summary: 'Senate approval revoked', detail: `${succeeded} result(s) revoked.`, life: 3000 });
    }
    if (failed) {
      toast.add({ severity: 'error', summary: 'Some approvals could not be revoked', detail: `${failed} failed. Please retry those rows.`, life: 5000 });
    }

    selectedRows.value = [];
    await refetch();
  } finally {
    bulkUnpublishing.value = false;
  }
}

/* ---------- EDIT ---------- */
const showEditDialog = ref(false);
const registrationToEdit = ref<StudentCourseRegistration | null>(null);

const editForm = reactive<{
    academic_calendar_id: string | null;
    semester: number | null;
    registration_level: string | null;
    programme_id: string | null;
    approval_status: string | null;
    senate_approval_status: string | null;
}>({
  academic_calendar_id: null,
  semester: null,
  registration_level: null,
  programme_id: null,
  approval_status: null,
  senate_approval_status: null,
});

// Snapshot of the values the dialog opened with, so confirmUpdate can submit
// ONLY the fields the user actually changed. Without this, an unrelated single
// field edit re-sends the prefilled approval_status — which for a legacy
// Title-case value (e.g. "Approved") the tightened backend enum rejects with a
// 422 (#357).
const originalEditValues = reactive<{
    academic_calendar_id: string | null;
    semester: number | null;
    registration_level: string | null;
    programme_id: string | null;
    approval_status: string | null;
    senate_approval_status: string | null;
}>({
  academic_calendar_id: null,
  semester: null,
  registration_level: null,
  programme_id: null,
  approval_status: null,
  senate_approval_status: null,
});

function handleEdit(row: StudentCourseRegistration) {
  clearErrors();
  registrationToEdit.value = row;
  editForm.academic_calendar_id = row.academic_calendar_id !== null && row.academic_calendar_id !== undefined
    ? String(row.academic_calendar_id)
    : null;
  editForm.semester = typeof row.semester === 'number' ? row.semester : null;
  editForm.registration_level = row.registration_level != null ? String(row.registration_level) : null;
  editForm.programme_id = row.programme_id != null ? String(row.programme_id) : null;
  editForm.approval_status = row.approval_status ?? null;
  editForm.senate_approval_status = row.senate_approval_status ?? null;
  Object.assign(originalEditValues, {
    academic_calendar_id: editForm.academic_calendar_id,
    semester: editForm.semester,
    registration_level: editForm.registration_level,
    programme_id: editForm.programme_id,
    approval_status: editForm.approval_status,
    senate_approval_status: editForm.senate_approval_status,
  });
  showEditDialog.value = true;
}

async function confirmUpdate() {
  if (!registrationToEdit.value) return;
  clearErrors();

  // Submit only the fields that actually changed. Untouched fields — in
  // particular a prefilled legacy Title-case approval_status — are never
  // re-sent, so an unrelated edit no longer 422s (#357).
  const fields = buildChangedRegistrationFields(editForm, originalEditValues);

  // Nothing changed — close without a pointless mutation.
  if (Object.keys(fields).length === 0) {
    showEditDialog.value = false;
    registrationToEdit.value = null;
    return;
  }

  try {
    const result = await updateRegistrationFields({
      course_registration_id: String(registrationToEdit.value.id),
      fields,
    });

    if (result) {
      showEditDialog.value = false;
      registrationToEdit.value = null;
      await refetch();
    }
  } catch (err) {
    handleServerErrors(err);
  }
}

// --- Swap Course ---
const showSwapDialog = ref(false);
const swapForm = reactive({
  programme_id: '',
  academic_calendar_id: '',
  old_course_id: '',
  new_course_id: '',
});
const { mutate: swapMutate, loading: swapping } = useMutation(BULK_SWAP_COURSE);

async function executeSwap() {
  if (!swapForm.programme_id || !swapForm.old_course_id || !swapForm.new_course_id) return;
  try {
    const vars: Record<string, unknown> = {
      programme_id: swapForm.programme_id,
      old_course_id: swapForm.old_course_id,
      new_course_id: swapForm.new_course_id,
    };
    if (swapForm.academic_calendar_id) vars.academic_calendar_id = swapForm.academic_calendar_id;

    const res = await swapMutate(vars);
    const data = res?.data?.bulkSwapCourse;
    if (data?.success) {
      toast.add({ severity: 'success', summary: 'Swapped', detail: data.message, life: 5000 });
      showSwapDialog.value = false;
      swapForm.old_course_id = '';
      swapForm.new_course_id = '';
      await refetch();
    } else {
      toast.add({ severity: 'warn', summary: 'Warning', detail: data?.message || 'Swap failed.', life: 5000 });
    }
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
  }
}
</script>
